import React, { useState } from 'react';
import Header from '@/components/layout/Header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Library as LibraryIcon, Music, FileAudio, FileVideo, Image, File } from 'lucide-react';
import MediaLibraryOrganizer from '@/components/library/MediaLibraryOrganizer';
import MediaLibrarySearch from '@/components/library/MediaLibrarySearch';
import MediaBulkActions from '@/components/library/MediaBulkActions';
import MediaUploadDialog from '@/components/library/MediaUploadDialog';
import MediaFilters from '@/components/library/MediaFilters';
import PopularTags from '@/components/library/PopularTags';
import EmptyMediaState from '@/components/library/EmptyMediaState';

const Library: React.FC = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  
  const handleTagToggle = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };
  
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };
  
  const handleFileUpload = (files: File[]) => {
    console.log('Uploading files:', files);
    // Handle file upload logic
  };
  
  const handleAIOrganize = () => {
    console.log('AI Organize clicked');
    // Handle AI organization logic
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-28 px-4 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 animate-fade-in">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
              <LibraryIcon className="h-8 w-8 text-blue-500" />
              Media Library
            </h1>
            <p className="text-muted-foreground">Organize and manage all your audio, video, and artwork files</p>
          </div>
          
          <div className="flex gap-2">
            <MediaLibrarySearch 
              onSearch={handleSearch}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onAIOrganize={handleAIOrganize}
              setShowUploadDialog={setShowUploadDialog}
              showUploadDialog={showUploadDialog}
            />
            <MediaUploadDialog 
              onClose={() => setShowUploadDialog(false)} 
              onUpload={handleFileUpload}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 animate-slide-up">
          <div className="lg:col-span-1 space-y-6">
            <MediaFilters 
              selectedTags={selectedTags} 
              onTagToggle={handleTagToggle}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
            <PopularTags 
              tags={['Music', 'Vocals', 'Beats', 'Cover Art', 'Remix', 'Instrumental', 'Demo', 'WIP', 'Final', 'Archive']}
              selectedTags={selectedTags}
              onTagToggle={handleTagToggle}
            />
          </div>
          
          <div className="lg:col-span-3">
            <div className="bg-card rounded-lg border shadow-sm">
              <Tabs defaultValue={activeTab} onValueChange={setActiveTab}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b">
                  <TabsList>
                    <TabsTrigger value="all" className="flex items-center gap-2">
                      <File className="h-4 w-4" />
                      <span>All Files</span>
                    </TabsTrigger>
                    <TabsTrigger value="audio" className="flex items-center gap-2">
                      <FileAudio className="h-4 w-4" />
                      <span>Audio</span>
                    </TabsTrigger>
                    <TabsTrigger value="artwork" className="flex items-center gap-2">
                      <Image className="h-4 w-4" />
                      <span>Artwork</span>
                    </TabsTrigger>
                    <TabsTrigger value="video" className="flex items-center gap-2">
                      <FileVideo className="h-4 w-4" />
                      <span>Video</span>
                    </TabsTrigger>
                  </TabsList>
                  
                  {selectedFiles.length > 0 && (
                    <MediaBulkActions 
                      selectedCount={selectedFiles.length} 
                      onClearSelection={() => setSelectedFiles([])} 
                      onDeleteSelected={() => console.log('Delete selected')}
                      onDownloadSelected={() => console.log('Download selected')}
                      onMoveSelected={() => console.log('Move selected')}
                      onTagSelected={() => console.log('Tag selected')}
                    />
                  )}
                </div>
                
                <TabsContent value="all" className="p-4">
                  <MediaLibraryOrganizer 
                    mediaType="all"
                    selectedFiles={selectedFiles}
                    onSelectFiles={setSelectedFiles}
                    searchQuery={searchQuery}
                  />
                </TabsContent>
                
                <TabsContent value="audio" className="p-4">
                  <MediaLibraryOrganizer 
                    mediaType="audio"
                    selectedFiles={selectedFiles}
                    onSelectFiles={setSelectedFiles}
                    searchQuery={searchQuery}
                  />
                </TabsContent>
                
                <TabsContent value="artwork" className="p-4">
                  <MediaLibraryOrganizer 
                    mediaType="artwork"
                    selectedFiles={selectedFiles}
                    onSelectFiles={setSelectedFiles}
                    searchQuery={searchQuery}
                  />
                </TabsContent>
                
                <TabsContent value="video" className="p-4">
                  <EmptyMediaState 
                    type="video"
                    message="No video files yet"
                    description="Upload music videos, performance clips, or promotional videos"
                    activeTab="video"
                    hasFilters={searchQuery.length > 0}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Library;
